import { useMemo, useRef, useState } from 'react';
import {
  CONSOLIDATION_BAND,
  MARKET_MAP,
  MARKET_MAP_TOTAL_CATEGORIES,
  MARKET_MAP_TOTAL_TOOLS,
} from '../lib/stackData';
import { defaultAgent, getRcrtClient, isRcrtConfigured } from '../lib/rcrt';
import { trackEvent } from '../lib/analytics';
import Arrow from './Arrow';

/**
 * 04 · Your stack, costed — agentic, JIT-UI interactive.
 *
 * The advisor never chats in free text: it answers with interactive cards
 * (rcrt's JIT UI — the same pattern the SDK's cards.resolve() powers in
 * production). Every interaction is a card action.
 *
 * Live mode (VITE_RCRT_* set): card bodies stream from the real advisor
 * agent via rcrt.chat.send + sessionStream — still button-driven only.
 * Preview mode: cards are generated deterministically from the real market
 * map data, and labelled as a simulation of the live advisor.
 */

interface SelTool {
  cat: string;
  name: string;
  lens?: 'replace' | 'integrate';
}

/** Real marketplace bundles that map to market-map categories (verified in-repo). */
const BUNDLE_MAP: Record<string, string> = {
  'Customer Support & Service': 'install support-concierge',
  'Meetings, Transcription & Voice': 'install meeting-minutes',
  'Knowledge & Enterprise Search': 'install sop-knowledge',
  'Sales Intelligence & Engagement': 'install sales-outreach',
  Construction: 'install gridlines',
  'Project & Work Management': 'install possibl-studio',
  'AI Agents & Automation': 'native runtime — agents, tools, schedules',
  'CRM & Sales': 'growth & bookings hub — pattern in production',
  'Marketing & Email': 'growth & bookings hub — pattern in production',
  'Forms & Document Automation': 'install assessment',
};

type CardKind = 'read' | 'plan' | 'keep' | 'sequence';

interface JitCard {
  kind: CardKind;
  title: string;
  lines: { l: string; r?: string; tone?: 'ok' | 'warn' | 'dim' }[];
  note?: string;
  actions: { id: CardKind | 'book'; label: string }[];
  streaming?: boolean;
  streamText?: string;
}

export default function StackCalculator() {
  const [openCat, setOpenCat] = useState<string | null>(null);
  const [selCats, setSelCats] = useState<Set<string>>(new Set());
  const [selTools, setSelTools] = useState<Set<string>>(new Set());
  const [cards, setCards] = useState<JitCard[]>([]);
  const [thinking, setThinking] = useState(false);
  const configured = isRcrtConfigured();
  const cardsRef = useRef<HTMLDivElement>(null);

  const toolKey = (cat: string, tool: string) => `${cat}::${tool}`;

  function toggleCat(name: string) {
    setSelCats((s) => {
      const next = new Set(s);
      if (next.has(name)) {
        next.delete(name);
        setSelTools((t) => {
          const nt = new Set(t);
          for (const k of nt) if (k.startsWith(`${name}::`)) nt.delete(k);
          return nt;
        });
      } else {
        next.add(name);
      }
      return next;
    });
    setCards([]); // selections changed — the advisor re-reads on demand
  }

  function toggleTool(cat: string, tool: string) {
    const k = toolKey(cat, tool);
    setSelTools((t) => {
      const next = new Set(t);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
    setCards([]);
  }

  const picked = useMemo<SelTool[]>(() => {
    const out: SelTool[] = [];
    for (const cat of MARKET_MAP) {
      const wholeCat = selCats.has(cat.name);
      for (const t of cat.tools) {
        if (wholeCat || selTools.has(toolKey(cat.name, t.name))) {
          out.push({ cat: cat.name, name: t.name, lens: t.lens });
        }
      }
    }
    return out;
  }, [selCats, selTools]);

  const tally = useMemo(() => {
    return {
      total: picked.length,
      replace: picked.filter((t) => t.lens === 'replace').length,
      keep: picked.filter((t) => t.lens === 'integrate').length,
      unmarked: picked.filter((t) => !t.lens).length,
      cats: new Set(picked.map((t) => t.cat)).size,
    };
  }, [picked]);

  /* ── deterministic card generation from the real map data ── */

  function buildCard(kind: CardKind): JitCard {
    if (kind === 'read') {
      const shown = picked.slice(0, 5);
      return {
        kind,
        title: "I've read your stack.",
        lines: [
          ...shown.map((t) => ({
            l: t.name,
            r: t.lens === 'replace' ? 'replace' : t.lens === 'integrate' ? 'keep' : 'discovery',
            tone: (t.lens === 'replace' ? 'ok' : t.lens === 'integrate' ? 'warn' : 'dim') as
              | 'ok'
              | 'warn'
              | 'dim',
          })),
          ...(picked.length > 5 ? [{ l: `+ ${picked.length - 5} more`, tone: 'dim' as const }] : []),
        ],
        note: `${tally.total} tools across ${tally.cats} categories — ${tally.replace} replace-marked, ${tally.keep} keep-and-integrate, ${tally.unmarked} for discovery. Typical consolidation of the replaceable layer: ${Math.round(CONSOLIDATION_BAND.low * 100)}–${Math.round(CONSOLIDATION_BAND.high * 100)}% (indicative).`,
        actions: [
          { id: 'plan', label: 'Map my consolidation' },
          { id: 'keep', label: 'What has to stay?' },
        ],
      };
    }
    if (kind === 'plan') {
      const cats = [...new Set(picked.filter((t) => t.lens !== 'integrate').map((t) => t.cat))];
      return {
        kind,
        title: 'Your consolidation map',
        lines: cats.slice(0, 7).map((c) => ({
          l: c,
          r: BUNDLE_MAP[c] ?? 'compose in Studio — one session',
          tone: (BUNDLE_MAP[c]?.startsWith('install') ? 'ok' : 'dim') as 'ok' | 'dim',
        })),
        note: 'Bundles named here exist in the marketplace today; everything else is composed in Studio — connectors in minutes, a full bundle in a session.',
        actions: [
          { id: 'sequence', label: 'Sequence it' },
          { id: 'book', label: 'Book a discovery session' },
        ],
      };
    }
    if (kind === 'keep') {
      const keeps = picked.filter((t) => t.lens === 'integrate');
      return {
        kind,
        title: 'What stays (and why)',
        lines: keeps.length
          ? keeps.slice(0, 6).map((t) => ({ l: t.name, r: 'system of record', tone: 'warn' as const }))
          : [{ l: 'Nothing you selected is keep-marked.', tone: 'dim' as const }],
        note: 'Systems of record stay in place — rcrt connects to them and orchestrates on top, so their spend is excluded from the consolidation range.',
        actions: [
          { id: 'plan', label: 'Map my consolidation' },
          { id: 'book', label: 'Book a discovery session' },
        ],
      };
    }
    // sequence
    return {
      kind,
      title: 'A sensible order',
      lines: [
        { l: '1 · Connect what stays', r: 'connectors — minutes each', tone: 'dim' },
        { l: '2 · Install matching bundles', r: '1 install each', tone: 'ok' },
        { l: '3 · Compose the custom pieces', r: 'Studio — a session each', tone: 'ok' },
        { l: '4 · Parallel-run, then cut over', r: 'confirmed in discovery', tone: 'warn' },
      ],
      note: 'Order and timing are indicative — the real sequence comes from a discovery session against your contracts and usage.',
      actions: [{ id: 'book', label: 'Book a Possibilities Session' }],
    };
  }

  /* ── live mode: stream the card body from the real advisor agent ── */

  async function streamCard(kind: CardKind, prompt: string) {
    const rcrt = getRcrtClient();
    if (!rcrt) return;
    const base = buildCard(kind);
    const card: JitCard = { ...base, lines: [], note: undefined, streaming: true, streamText: '' };
    setCards((c) => [...c.filter((x) => x.kind !== kind), card]);
    try {
      const res = await rcrt.chat.send({ message: prompt, target_agent: defaultAgent() });
      let buf = '';
      rcrt.chat.sessionStream(res.session_id, {
        onDelta: ({ delta }) => {
          if (!delta) return;
          buf += delta;
          setCards((c) => c.map((x) => (x.kind === kind ? { ...x, streamText: buf } : x)));
        },
        onClose: () =>
          setCards((c) => c.map((x) => (x.kind === kind ? { ...x, streaming: false } : x))),
        onError: () =>
          setCards((c) => c.map((x) => (x.kind === kind ? { ...x, streaming: false } : x))),
      });
    } catch {
      setCards((c) => c.map((x) => (x.kind === kind ? { ...base, streaming: false } : x)));
    }
  }

  function stackSummary() {
    return picked
      .map((t) => `${t.name} (${t.cat}${t.lens ? `, map-lens: ${t.lens}` : ''})`)
      .join('; ');
  }

  const PROMPTS: Record<CardKind, string> = {
    read: `Read this SaaS stack and summarise it in 4 short lines (counts, what's replaceable on rcrt, what stays). No questions, no greetings. Stack: ${stackSummary()}`,
    plan: `Map this stack's replaceable tools to rcrt marketplace bundles or Studio builds, one line each, max 7 lines. Stack: ${stackSummary()}`,
    keep: `List which of these tools are systems of record that should stay (rcrt integrates on top), one line each with a short reason. Stack: ${stackSummary()}`,
    sequence: `Give a 4-step consolidation order for moving this stack onto rcrt, one short line per step. Stack: ${stackSummary()}`,
  };

  function act(kind: CardKind | 'book') {
    if (kind === 'book') {
      trackEvent('cta_click', { cta: 'book_session', location: 'calculator' });
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (configured) {
      streamCard(kind, PROMPTS[kind]);
      return;
    }
    // preview: deterministic, with a brief "thinking" beat so the JIT rhythm reads
    setThinking(true);
    setTimeout(() => {
      setCards((c) => [...c.filter((x) => x.kind !== kind), buildCard(kind)]);
      setThinking(false);
      requestAnimationFrame(() => {
        cardsRef.current?.scrollTo({ top: cardsRef.current.scrollHeight, behavior: 'smooth' });
      });
    }, 650);
  }

  return (
    <section id="calculator">
      <div className="wrap">
        <div className="sec-head">
          <div className="sec-num">04 · Your stack, costed</div>
          <div>
            <h2>
              What could <span className="ph">rcrt</span> replace?
            </h2>
            <p className="lede">
              The 2026 SaaS Market Map covers the {MARKET_MAP_TOTAL_TOOLS} tools across{' '}
              {MARKET_MAP_TOTAL_CATEGORIES} categories that make up most business stacks.
              Pick what you run, then let the advisor respond — with rcrt's JIT UI, not a
              chat box. Every answer is a card you can act on.
            </p>
          </div>
        </div>

        <div className="calc">
          <div className="calc-grid">
            <div>
              <h3>Tell us what you run</h3>
              <p className="sub">
                Tick a category, or expand it and pick exact tools. Each tool carries the
                map's lens: replace, or keep-and-integrate.
              </p>

              <div className="acc">
                {MARKET_MAP.map((cat) => {
                  const open = openCat === cat.name;
                  const sel = selCats.has(cat.name);
                  return (
                    <div key={cat.name} className={`acat ${open ? 'open' : ''} ${sel ? 'sel' : ''}`}>
                      <div className="acat-head" onClick={() => setOpenCat(open ? null : cat.name)}>
                        <span
                          className="acat-cb"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCat(cat.name);
                          }}
                        />
                        <span className="acat-name">{cat.name}</span>
                        <span className="acat-meta">{cat.tools.length} tools</span>
                        <span className="acat-chev">›</span>
                      </div>
                      <div className="acat-body">
                        {cat.tools.map((t) => {
                          const on = sel || selTools.has(toolKey(cat.name, t.name));
                          return (
                            <div
                              key={t.name}
                              className={`tool ${on ? 'on' : ''}`}
                              onClick={() => !sel && toggleTool(cat.name, t.name)}
                              title={t.what}
                            >
                              <span className="tool-cb" />
                              <span className="tool-name">{t.name}</span>
                              <span
                                className={`tool-px ${
                                  t.lens === 'replace' ? 'replace' : t.lens === 'integrate' ? 'keep' : ''
                                }`}
                              >
                                {t.lens === 'replace'
                                  ? 'replace'
                                  : t.lens === 'integrate'
                                    ? 'keep'
                                    : 'discovery'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="ai-note">
                "discovery" means the map doesn't pre-judge that tool — whether rcrt replaces
                it is confirmed against your usage in a discovery session.
              </p>
            </div>

            {/* ── the advisor: JIT UI surface ── */}
            <div className="result jit">
              <div className="jit-head">
                <span className="jit-title">
                  <span className="ph">[=]</span> advisor
                </span>
                <span className="jit-mode">
                  {configured ? 'live · generated by the agent' : 'preview · simulated advisor'}
                </span>
              </div>

              <div className="jit-tally">
                <span>
                  <b>{tally.total}</b> tools
                </span>
                <span className="ok">
                  <b>{tally.replace}</b> replace
                </span>
                <span className="warn">
                  <b>{tally.keep}</b> keep
                </span>
                <span>
                  <b>{tally.unmarked}</b> discovery
                </span>
              </div>

              <div className="jit-cards" ref={cardsRef}>
                {tally.total === 0 && (
                  <div className="jit-hint">
                    Select what you run — the advisor answers with cards, not chat. Actions,
                    not typing.
                  </div>
                )}

                {tally.total > 0 && cards.length === 0 && !thinking && (
                  <button className="btn btn-primary jit-cta" onClick={() => act('read')}>
                    Read my stack
                    <Arrow />
                  </button>
                )}

                {cards.map((c) => (
                  <div key={c.kind} className="jit-card">
                    <div className="jit-card-title">{c.title}</div>
                    {c.streamText !== undefined ? (
                      <div className="jit-stream">{c.streamText || '…'}</div>
                    ) : (
                      <div className="jit-rows">
                        {c.lines.map((ln, i) => (
                          <div key={i} className="jit-row">
                            <span className="jl">{ln.l}</span>
                            {ln.r && <span className={`jr ${ln.tone ?? ''}`}>{ln.r}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {c.note && <div className="jit-note">{c.note}</div>}
                    {!c.streaming && (
                      <div className="jit-actions">
                        {c.actions.map((a) => (
                          <button
                            key={a.id}
                            className={a.id === 'book' ? 'primary' : ''}
                            onClick={() => act(a.id)}
                          >
                            {a.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {thinking && (
                  <div className="jit-thinking">
                    <span className="spinner" /> composing a card…
                  </div>
                )}
              </div>

              <div className="jit-foot">
                This panel is rcrt's JIT UI: agents answer with interactive cards — the same
                pattern the SDK's <code>cards.resolve()</code> powers in production.
                {!configured && ' In this preview the advisor is simulated deterministically from the market map.'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
